"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import DocumentCardsView from "@/components/optilase/hub/document-cards-view";
import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { regionFromPathname } from "@/lib/region";
import { cn } from "@/lib/utils";
import { toDocumentCards, type DocumentCardBlok } from "@/lib/document-card";
import { usePublicPathname } from "@/lib/public-pathname";

type DocumentCardsBlok = SbBlokData & {
  heading?: string;
  intro?: string;
  columns?: string;
  cards?: DocumentCardBlok[];
  bg_color?: unknown;
  card_color?: unknown;
};

export default function DocumentCards({ blok }: { blok: DocumentCardsBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const surface = surfaceFrom(blok.bg_color);
  const cardSurface = surfaceFrom(blok.card_color);
  const cards = toDocumentCards(blok.cards, region);

  if (cards.length === 0) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 lg:gap-12">
        {(blok.heading || blok.intro) && (
          <div className="flex max-w-201.5 flex-col gap-5">
            {blok.heading && (
              <h2 className={cn("text-section", headingOn(surface, "text-teal-07"))}>
                {blok.heading}
              </h2>
            )}
            {blok.intro && (
              <p
                className={cn(
                  "text-base leading-normal lg:text-xl",
                  onSurface(surface, "text-white/85", "text-grey-06", "text-grey-06"),
                )}
              >
                {blok.intro}
              </p>
            )}
          </div>
        )}

        <DocumentCardsView
          cards={cards}
          columns={blok.columns}
          label={blok.heading || "Documents"}
          surface={surface}
          cardSurface={cardSurface}
        />
      </div>
    </section>
  );
}
